/**
 * TealerLab — Judge.me review avatars.
 *
 * Judge.me ships `.jdgm-rev__icon` as a bare disc: the server-rendered markup
 * holds the reviewer's initial, but their setup pass empties the node again as
 * soon as it hydrates a review — and it re-runs on every sort, filter, tab
 * switch and pagination fetch. So the initial is (re)written here from
 * `.jdgm-rev__author`, and the "already done" test reads the node itself
 * instead of a flag: a flag survives their wipe, the letter does not, which is
 * exactly how the discs ended up permanently empty.
 *
 * The tone is derived from the name so the same reviewer always gets the same
 * disc; the colours live in tlr-jdgm-avatars.css, keyed by the attribute set
 * here. No listener is attached to any Judge.me node — their own clicks are
 * bound by delegation on an ancestor and must never be intercepted.
 */

const CONTAINERS = '.jdgm-widget, .jdgm-all-reviews-page';
const TONES = 6;
const LETTER = /[\p{L}\p{N}]/u;

/** First letter or digit of the name — an emoji or a quote is not a monogram. */
function initial(name) {
  for (const character of name) {
    if (LETTER.test(character)) return character.toUpperCase();
  }
  return '';
}

/** Same name, same tone, on every page and after every re-render. */
function tone(name) {
  let hash = 0;
  for (let index = 0; index < name.length; index += 1) hash = (hash * 31 + name.charCodeAt(index)) | 0;
  return String(Math.abs(hash) % TONES);
}

function paint(root) {
  root.querySelectorAll('.jdgm-rev__icon').forEach((icon) => {
    // A real reviewer picture, as an <img> or as the background Judge.me
    // paints on the disc, wins over the monogram.
    if (icon.querySelector('img') || icon.style.backgroundImage) return;
    const review = icon.closest('.jdgm-rev');
    const author = review && review.querySelector('.jdgm-rev__author');
    const name = author ? author.textContent.trim() : '';
    if (!name) return;
    const letter = initial(name);
    if (!letter) return;
    if (icon.textContent.trim() === letter && icon.dataset.tlrAvatarTone) return;
    icon.dataset.tlrAvatarTone = tone(name);
    icon.textContent = letter;
    // The reviewer name sits right next to the disc: nothing to announce twice.
    icon.setAttribute('aria-hidden', 'true');
  });
}

/** One observer per Judge.me container, never on the document. */
function mount(root) {
  if (root.dataset.tlrAvatars) return;
  root.dataset.tlrAvatars = '1';
  let frame = 0;
  const schedule = () => {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      paint(root);
    });
  };
  paint(root);
  new MutationObserver(schedule).observe(root, { childList: true, subtree: true });
}

function scan(scope) {
  (scope || document).querySelectorAll(CONTAINERS).forEach(mount);
}

scan();
// The theme editor re-renders a section: the markup comes back, the observer
// bound to the old container does not.
document.addEventListener('shopify:section:load', (event) => scan(event.target));
